import {
  SLIDES_CONTAINER_ID,
  useSlidesLibraryStoreShallow,
} from "@modules/slides-library/shared";
import { useCallback, useEffect, useRef } from "react";

export const useTemplatesIntersection = ({ loaded }: { loaded: boolean }) => {
  const lastUpdateTime = useRef<number>(0);
  const lastScrollY = useRef<number>(0);
  const scrollingDown = useRef<boolean>(true);
  const {
    activeTemplateCategoryId,
    isManualScrolling,
    isInSearch,
    setActiveTemplateCategoryId,
  } = useSlidesLibraryStoreShallow((s) => ({
    activeTemplateCategoryId: s.activeTemplateCategoryId,
    isManualScrolling: s.isManualScrolling,
    isInSearch: !!s.search,
    setActiveTemplateCategoryId: s.setActiveTemplateCategoryId,
  }));

  const scrollToCategory = useCallback(
    (id: string) => {
      // Find the category element
      const element = document.querySelector(
        `[data-category-id="category:${id}"]`,
      ) as HTMLElement;
      if (!element) return;

      // Update active category ID
      setActiveTemplateCategoryId(id);

      // Scroll to the element
      const container = document.getElementById(SLIDES_CONTAINER_ID);
      const viewport = container?.querySelector(
        "[data-radix-scroll-area-viewport]",
      );

      if (viewport) {
        // Set last update time to prevent intersection observer from immediately overriding
        lastUpdateTime.current = Date.now();

        // Scroll to the element
        const elementTop = element.getBoundingClientRect().top;
        const viewportTop = viewport.getBoundingClientRect().top;
        viewport.scrollTo({
          top: viewport.scrollTop + (elementTop - viewportTop) - 8,
          behavior: "smooth",
        });
      }
    },
    [setActiveTemplateCategoryId],
  );

  useEffect(() => {
    if (!loaded) return;

    // Set initial active category ID if not already set
    if (!activeTemplateCategoryId && !isInSearch) {
      const container = document.getElementById(SLIDES_CONTAINER_ID);
      if (container) {
        const categories =
          container.querySelectorAll<HTMLElement>("[data-category-id]");
        if (categories.length > 0) {
          const firstCategoryId = categories[0]
            .getAttribute("data-category-id")
            ?.split(":")[1];
          if (firstCategoryId) {
            setActiveTemplateCategoryId(firstCategoryId);
          }
        }
      }
    }

    const container = document.getElementById(SLIDES_CONTAINER_ID);
    if (!container) return;

    const viewport = container.querySelector(
      "[data-radix-scroll-area-viewport]",
    ) as HTMLElement;
    if (!viewport) return;

    // Track scroll position and direction
    const handleScroll = () => {
      const currentScrollY = viewport.scrollTop;
      scrollingDown.current = currentScrollY > lastScrollY.current;
      lastScrollY.current = currentScrollY;

      // Skip updates if manual scrolling is in progress
      if (isManualScrolling) return;

      const viewportRect = viewport.getBoundingClientRect();
      const categories =
        container.querySelectorAll<HTMLElement>("[data-category-id]");

      // 100px from top of viewport
      const targetPosition = viewportRect.top + 50;

      let closestCategory: HTMLElement | null = null;
      let minDistance = Infinity;

      categories.forEach((category) => {
        const titleElement = category.querySelector(".--title") as HTMLElement;
        if (!titleElement) return;

        const titleRect = titleElement.getBoundingClientRect();
        const distanceFromTarget = Math.abs(titleRect.top - targetPosition);

        if (distanceFromTarget < minDistance) {
          minDistance = distanceFromTarget;
          closestCategory = category;
        }
      });

      if (closestCategory) {
        const categoryId = (closestCategory as HTMLElement)
          .getAttribute("data-category-id")
          ?.split(":")[1];
        if (categoryId && categoryId !== activeTemplateCategoryId) {
          setActiveTemplateCategoryId(categoryId);
        }
      }
    };

    viewport.addEventListener("scroll", handleScroll, { passive: true });

    // Initial check for active category
    handleScroll();

    return () => {
      viewport.removeEventListener("scroll", handleScroll);
    };
  }, [loaded, isInSearch, activeTemplateCategoryId, isManualScrolling]);

  return { scrollToCategory };
};
